import React, { useState, useEffect } from 'react';
import {
  Sparkles, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  DollarSign, Users, BarChart3, PieChart, RefreshCw, Brain,
  Lightbulb, Target, Shield, Clock, ArrowRight, ChevronRight,
  AlertCircle, Info, Zap, FileText, Calculator, BrainCircuit, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { aiService } from '../../../lib/aiService';
import { toast } from 'sonner';

// Smart local analysis function (used when AI is unavailable or as fallback)
const analyzePayrollDataLocally = (data) => {
  const {
    payrollItems,
    employees,
    compensation,
    previousPayrolls,
    taxLiabilities,
    currentMonth
  } = data;

  // Handle empty data gracefully
  if (!payrollItems || payrollItems.length === 0) {
    return {
      summary: {
        currentPayroll: 0,
        previousPayroll: 0,
        percentChange: 0,
        employeeCount: 0,
        avgPayPerEmployee: 0
      },
      anomalies: [],
      taxCompliance: [],
      departmentBreakdown: {},
      optimizations: [],
      forecast: {
        monthlyAverage: 0,
        yearEndProjection: 0,
        remainingPayroll: 0,
        remainingMonths: 12 - currentMonth
      },
      healthScore: 100,
      aiPowered: false,
      message: "No payroll data to analyze. Run a payroll to see insights."
    };
  }

  // Calculate key metrics
  const currentTotal = payrollItems.reduce((sum, i) => sum + (i.grossPay || 0), 0);
  const previousTotal = previousPayrolls.length > 0 
    ? previousPayrolls.reduce((sum, i) => sum + (i.grossPay || 0), 0) / Math.max(previousPayrolls.length / payrollItems.length, 1)
    : 0;
  const percentChange = previousTotal > 0 ? ((currentTotal - previousTotal) / previousTotal) * 100 : 0;

  // Detect anomalies
  const anomalies = [];
  const avgPay = currentTotal / payrollItems.length;
  
  payrollItems.forEach(item => {
    const emp = employees.find(e => e.id === item.userId);
    const empName = emp ? `${emp.profile?.firstName || ''} ${emp.profile?.lastName || ''}`.trim() || 'Unknown' : 'Unknown';
    const empComp = compensation.find(c => c.userId === item.userId);
    
    // High variance detection (more than 50% above average)
    if (item.grossPay > avgPay * 1.5 && avgPay > 0) {
      anomalies.push({
        type: 'high_pay',
        severity: 'warning',
        employee: empName,
        amount: item.grossPay,
        message: `Pay of ${formatUSDCurrency(item.grossPay)} is ${((item.grossPay / avgPay - 1) * 100).toFixed(0)}% above average - verify if intentional`
      });
    }
    
    // Low pay detection (more than 50% below average)
    if (item.grossPay < avgPay * 0.5 && item.grossPay > 0 && avgPay > 0) {
      anomalies.push({
        type: 'low_pay',
        severity: 'info',
        employee: empName,
        amount: item.grossPay,
        message: `Pay of ${formatUSDCurrency(item.grossPay)} is ${((1 - item.grossPay / avgPay) * 100).toFixed(0)}% below average - verify part-time or pro-rated`
      });
    }
    
    // Missing or very low tax withholding
    const totalTax = (item.federalTax || 0) + (item.stateTax || 0) + (item.socialSecurity || 0) + (item.medicare || 0);
    if (item.grossPay > 2000 && totalTax < item.grossPay * 0.1) {
      anomalies.push({
        type: 'low_tax',
        severity: 'critical',
        employee: empName,
        message: `Tax withholding (${formatUSDCurrency(totalTax)}) seems low for gross of ${formatUSDCurrency(item.grossPay)} - check W-4 settings`
      });
    }

    // Overtime detection
    if (item.overtimePay && item.overtimePay > (item.regularPay || item.grossPay) * 0.3) {
      anomalies.push({
        type: 'high_overtime',
        severity: 'info',
        employee: empName,
        amount: item.overtimePay,
        message: `Overtime (${formatUSDCurrency(item.overtimePay)}) is ${((item.overtimePay / (item.regularPay || item.grossPay)) * 100).toFixed(0)}% of regular pay`
      });
    }
  });

  // Tax compliance check
  const taxCompliance = [];
  const pendingTaxes = (taxLiabilities || []).filter(t => t.status === 'pending' || t.status === 'due');
  const overdueTaxes = (taxLiabilities || []).filter(t => t.status === 'overdue');
  
  if (overdueTaxes.length > 0) {
    taxCompliance.push({
      type: 'overdue_taxes',
      severity: 'critical',
      count: overdueTaxes.length,
      total: overdueTaxes.reduce((sum, t) => sum + (t.amount || 0), 0),
      message: `${overdueTaxes.length} OVERDUE tax payments totaling ${formatUSDCurrency(overdueTaxes.reduce((sum, t) => sum + (t.amount || 0), 0))} - immediate action required!`
    });
  }
  
  if (pendingTaxes.length > 0) {
    taxCompliance.push({
      type: 'pending_taxes',
      severity: 'warning',
      count: pendingTaxes.length,
      total: pendingTaxes.reduce((sum, t) => sum + (t.amount || 0), 0),
      message: `${pendingTaxes.length} pending tax payments totaling ${formatUSDCurrency(pendingTaxes.reduce((sum, t) => sum + (t.amount || 0), 0))}`
    });
  }

  // Department analysis
  const deptBreakdown = {};
  payrollItems.forEach(item => {
    const emp = employees.find(e => e.id === item.userId);
    const dept = emp?.profile?.department || 'Unassigned';
    if (!deptBreakdown[dept]) {
      deptBreakdown[dept] = { count: 0, total: 0, avgPay: 0 };
    }
    deptBreakdown[dept].count++;
    deptBreakdown[dept].total += item.grossPay || 0;
  });
  
  Object.keys(deptBreakdown).forEach(dept => {
    deptBreakdown[dept].avgPay = deptBreakdown[dept].total / deptBreakdown[dept].count;
  });

  // Cost optimization suggestions
  const optimizations = [];
  
  // High overtime suggestion
  const totalOvertime = payrollItems.reduce((sum, i) => sum + (i.overtimePay || 0), 0);
  if (totalOvertime > currentTotal * 0.08) {
    optimizations.push({
      title: 'Reduce Overtime Costs',
      description: `Overtime is ${((totalOvertime / currentTotal) * 100).toFixed(1)}% of payroll (${formatUSDCurrency(totalOvertime)}). Consider hiring additional staff or redistributing workload.`,
      potentialSavings: totalOvertime * 0.25,
      priority: 'high'
    });
  }

  // Tax efficiency check
  const avgTaxRate = payrollItems.reduce((sum, i) => sum + ((i.federalTax || 0) + (i.stateTax || 0)), 0) / Math.max(currentTotal, 1);
  if (avgTaxRate > 0.30) {
    optimizations.push({
      title: 'Review Tax Withholding Rates',
      description: `Average income tax withholding is ${(avgTaxRate * 100).toFixed(1)}%. Verify W-4 configurations are accurate to avoid over-withholding.`,
      priority: 'medium'
    });
  }

  // Benefit optimization
  const avgBenefitCost = payrollItems.reduce((sum, i) => sum + (i.benefitDeductions || 0), 0) / payrollItems.length;
  if (avgBenefitCost > avgPay * 0.15) {
    optimizations.push({
      title: 'Review Benefit Costs',
      description: `Average benefit deductions are ${((avgBenefitCost / avgPay) * 100).toFixed(1)}% of gross pay. Consider reviewing benefit plans during open enrollment.`,
      priority: 'low'
    });
  }

  // Forecasting
  const monthlyAvg = currentTotal;
  const yearEndProjection = monthlyAvg * 12;
  const remainingMonths = 12 - currentMonth;
  const projectedRemaining = monthlyAvg * remainingMonths;

  return {
    summary: {
      currentPayroll: currentTotal,
      previousPayroll: previousTotal,
      percentChange,
      employeeCount: payrollItems.length,
      avgPayPerEmployee: avgPay
    },
    anomalies,
    taxCompliance,
    departmentBreakdown: deptBreakdown,
    optimizations,
    forecast: {
      monthlyAverage: monthlyAvg,
      yearEndProjection,
      remainingPayroll: projectedRemaining,
      remainingMonths
    },
    healthScore: calculateHealthScore(anomalies, taxCompliance, optimizations),
    aiPowered: false
  };
};

const calculateHealthScore = (anomalies, taxCompliance, optimizations) => {
  let score = 100;
  
  // Deduct for anomalies
  (anomalies || []).forEach(a => {
    if (a.severity === 'critical') score -= 15;
    else if (a.severity === 'warning') score -= 8;
    else score -= 3;
  });
  
  // Deduct for tax compliance issues
  (taxCompliance || []).forEach(t => {
    if (t.severity === 'critical') score -= 20;
    else if (t.severity === 'warning') score -= 10;
  });
  
  // Deduct for optimization opportunities (minor)
  score -= (optimizations || []).length * 2;
  
  return Math.max(0, Math.min(100, score));
};

// Merge AI analysis with local analysis for best results
const mergeAnalysis = (localAnalysis, aiAnalysis) => {
  if (!aiAnalysis) return localAnalysis;
  
  return {
    ...localAnalysis,
    // Use AI insights for recommendations if available
    optimizations: aiAnalysis.optimizations?.length > 0 
      ? [...localAnalysis.optimizations, ...aiAnalysis.optimizations.filter(ai => 
          !localAnalysis.optimizations.some(local => local.title === ai.title)
        )]
      : localAnalysis.optimizations,
    // Add AI-specific forecast recommendations
    forecast: {
      ...localAnalysis.forecast,
      aiRecommendation: aiAnalysis.forecast?.recommendation || null
    },
    aiPowered: true,
    aiInsights: aiAnalysis.summary || null
  };
};

export default function PayrollAIInsights() {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    // Check if AI service is available
    setAiAvailable(aiService.isConfigured());
    loadAndAnalyze();
  }, []);

  const loadAndAnalyze = async () => {
    setLoading(true);
    try {
      const [
        payrollItems,
        employees,
        compensation,
        taxLiabilities,
        payrollRuns
      ] = await Promise.all([
        db.payrollRunItems.toArray(),
        db.users.toArray(),
        db.employeeCompensation.toArray(),
        db.taxLiabilities.toArray(),
        db.payrollRuns.toArray()
      ]);

      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const monthStart = new Date(currentYear, currentMonth - 1, 1).getTime();
      const monthEnd = new Date(currentYear, currentMonth, 0).getTime();
      
      // Current month items - or use recent items if no current month data
      const currentItems = payrollItems.filter(i => 
        i.createdAt >= monthStart && i.createdAt <= monthEnd
      );
      
      // Previous months for comparison
      const previousItems = payrollItems.filter(i => i.createdAt < monthStart);
      
      // Use most recent payroll run items if no current month data
      const itemsToAnalyze = currentItems.length > 0 
        ? currentItems 
        : payrollItems.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 50);

      const analysisData = {
        payrollItems: itemsToAnalyze,
        employees,
        compensation,
        previousPayrolls: previousItems,
        taxLiabilities,
        currentMonth
      };

      // First, do local analysis (fast, always works)
      let localResult = analyzePayrollDataLocally(analysisData);

      // If AI service is available, try to get AI-enhanced insights
      if (aiService.isConfigured() && itemsToAnalyze.length > 0) {
        try {
          const aiResult = await aiService.analyzePayroll({
            totalPayroll: localResult.summary.currentPayroll,
            averagePay: localResult.summary.avgPayPerEmployee,
            employeeCount: localResult.summary.employeeCount,
            departments: Object.keys(localResult.departmentBreakdown),
            anomalyCount: localResult.anomalies.length,
            pendingTaxes: localResult.taxCompliance.length
          });
          
          // Merge AI insights with local analysis
          localResult = mergeAnalysis(localResult, aiResult);
          setAiAvailable(true);
        } catch (aiError) {
          console.warn('AI analysis not available, using local analysis:', aiError.message);
          setAiAvailable(false);
        }
      }

      setAnalysis(localResult);
    } catch (error) {
      console.error('Error analyzing payroll:', error);
      toast.error('Failed to analyze payroll data');
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalysis = async () => {
    setAnalyzing(true);
    await loadAndAnalyze();
    setAnalyzing(false);
    toast.success('Analysis refreshed');
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 80) return 'bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'warning': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'info': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="relative">
              <Brain className="w-12 h-12 text-teal-600 animate-pulse" />
              <Sparkles className="w-5 h-5 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
            </div>
            <p className="text-gray-500">Analyzing payroll data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No payroll data available for analysis</p>
          <Button variant="outline" className="mt-4" onClick={refreshAnalysis}>
            <RefreshCw className="w-4 h-4 mr-2" /> Retry Analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <BrainCircuit className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                {analysis?.aiPowered ? 'AI' : 'Smart'} Payroll Insights
                {analysis?.aiPowered ? (
                  <Sparkles className="w-5 h-5 text-amber-300" />
                ) : (
                  <Calculator className="w-5 h-5 text-violet-300" />
                )}
              </h2>
              <p className="text-violet-200 text-sm">
                {analysis?.aiPowered 
                  ? 'AI-powered analysis and recommendations' 
                  : 'Intelligent analysis based on your payroll data'}
                {analysis?.aiPowered && (
                  <Badge className="ml-2 bg-emerald-500 text-white text-xs">AI Active</Badge>
                )}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-white hover:bg-white/20"
            onClick={refreshAnalysis}
            disabled={analyzing}
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <CardContent className="p-6">
        {/* No data message */}
        {analysis?.message && (
          <Alert className="mb-6 border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-800">Information</AlertTitle>
            <AlertDescription className="text-blue-700">{analysis.message}</AlertDescription>
          </Alert>
        )}

        {/* Health Score & Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Health Score */}
          <Card className={`${getHealthBg(analysis.healthScore)} border`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Payroll Health</p>
                  <p className={`text-3xl font-bold ${getHealthColor(analysis.healthScore)}`}>
                    {analysis.healthScore}%
                  </p>
                </div>
                <Shield className={`w-10 h-10 ${getHealthColor(analysis.healthScore)}`} />
              </div>
              <Progress 
                value={analysis.healthScore} 
                className="mt-2 h-2"
              />
            </CardContent>
          </Card>

          {/* Trend */}
          <Card className="border-0 bg-gray-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Month vs Previous</p>
                  <p className="text-2xl font-bold flex items-center gap-1">
                    {analysis.summary.percentChange >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-500" />
                    )}
                    {Math.abs(analysis.summary.percentChange).toFixed(1)}%
                  </p>
                </div>
                <BarChart3 className="w-10 h-10 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          {/* Anomalies */}
          <Card className={`border-0 ${analysis.anomalies.length > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Anomalies Detected</p>
                  <p className="text-2xl font-bold">{analysis.anomalies.length}</p>
                </div>
                {analysis.anomalies.length > 0 ? (
                  <AlertTriangle className="w-10 h-10 text-amber-500" />
                ) : (
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Optimizations */}
          <Card className="border-0 bg-blue-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Optimizations</p>
                  <p className="text-2xl font-bold">{analysis.optimizations.length}</p>
                </div>
                <Lightbulb className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="anomalies">
              Anomalies
              {analysis.anomalies.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs">
                  {analysis.anomalies.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="optimizations">Optimize</TabsTrigger>
            <TabsTrigger value="forecast">Forecast</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Payroll Summary */}
              <Card className="border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    Current Period Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Payroll</span>
                    <span className="font-semibold">{formatUSDCurrency(analysis.summary.currentPayroll)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Employees Paid</span>
                    <span className="font-semibold">{analysis.summary.employeeCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Pay/Employee</span>
                    <span className="font-semibold">{formatUSDCurrency(analysis.summary.avgPayPerEmployee)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Previous Period</span>
                    <span className="text-gray-500">{formatUSDCurrency(analysis.summary.previousPayroll)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Department Breakdown */}
              <Card className="border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <PieChart className="w-4 h-4 text-blue-600" />
                    Department Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(analysis.departmentBreakdown).slice(0, 4).map(([dept, data]) => (
                      <div key={dept} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                          <span className="text-sm text-gray-600">{dept}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-sm">{formatUSDCurrency(data.total)}</span>
                          <span className="text-xs text-gray-500 ml-2">({data.count})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tax Compliance Alert */}
            {analysis.taxCompliance.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Tax Compliance Alert</AlertTitle>
                <AlertDescription className="text-amber-700">
                  {analysis.taxCompliance[0].message}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          {/* Anomalies Tab */}
          <TabsContent value="anomalies" className="space-y-3">
            {analysis.anomalies.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No Anomalies Detected</p>
                <p className="text-sm text-gray-500">Your payroll data looks healthy!</p>
              </div>
            ) : (
              analysis.anomalies.map((anomaly, index) => (
                <Alert key={index} className={getSeverityColor(anomaly.severity)}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle className="flex items-center gap-2">
                    {anomaly.employee}
                    <Badge variant="outline" className="text-xs">
                      {anomaly.type.replace('_', ' ')}
                    </Badge>
                  </AlertTitle>
                  <AlertDescription>{anomaly.message}</AlertDescription>
                </Alert>
              ))
            )}
          </TabsContent>

          {/* Optimizations Tab */}
          <TabsContent value="optimizations" className="space-y-3">
            {analysis.optimizations.length === 0 ? (
              <div className="text-center py-8">
                <Zap className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Fully Optimized!</p>
                <p className="text-sm text-gray-500">No optimization suggestions at this time.</p>
              </div>
            ) : (
              analysis.optimizations.map((opt, index) => (
                <Card key={index} className="border hover:border-teal-300 transition-colors">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Lightbulb className="w-4 h-4 text-amber-500" />
                          <h4 className="font-medium">{opt.title}</h4>
                          <Badge variant={opt.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                            {opt.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{opt.description}</p>
                      </div>
                      {opt.potentialSavings && (
                        <div className="text-right ml-4">
                          <p className="text-xs text-gray-500">Potential Savings</p>
                          <p className="font-semibold text-emerald-600">{formatUSDCurrency(opt.potentialSavings)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Forecast Tab */}
          <TabsContent value="forecast" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="pt-4">
                  <p className="text-sm text-blue-600">Monthly Average</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatUSDCurrency(analysis.forecast.monthlyAverage)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border bg-gradient-to-br from-violet-50 to-violet-100">
                <CardContent className="pt-4">
                  <p className="text-sm text-violet-600">Year-End Projection</p>
                  <p className="text-2xl font-bold text-violet-900">
                    {formatUSDCurrency(analysis.forecast.yearEndProjection)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border bg-gradient-to-br from-emerald-50 to-emerald-100">
                <CardContent className="pt-4">
                  <p className="text-sm text-emerald-600">Remaining ({analysis.forecast.remainingMonths} months)</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {formatUSDCurrency(analysis.forecast.remainingPayroll)}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target className="w-4 h-4 text-teal-600" />
                  {analysis.aiPowered ? 'AI Recommendations' : 'Smart Recommendations'}
                  {analysis.aiPowered && <Badge className="text-xs bg-purple-100 text-purple-700">AI</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {/* AI-powered recommendation if available */}
                  {analysis.forecast.aiRecommendation && (
                    <li className="flex items-start gap-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-purple-800">{analysis.forecast.aiRecommendation}</span>
                    </li>
                  )}
                  {/* Default smart recommendations */}
                  {analysis.forecast.monthlyAverage > 0 && (
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Budget {formatUSDCurrency(analysis.forecast.monthlyAverage * 1.05)} monthly for contingency (+5%)</span>
                    </li>
                  )}
                  {analysis.taxCompliance.length > 0 && (
                    <li className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Address {analysis.taxCompliance.length} pending tax compliance item(s) immediately</span>
                    </li>
                  )}
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Schedule quarterly tax reviews to ensure compliance</span>
                  </li>
                  {analysis.optimizations.length > 0 && (
                    <li className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Review {analysis.optimizations.length} optimization opportunity(s) to reduce costs</span>
                    </li>
                  )}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
